<template>
    <vx-expandable-card>
        <template v-slot:header> From </template>
        <template v-slot:content>
            <vx-alert v-if="validate.groups.$errors.length" class="mt-2 mb-6" :closeable="false" color="danger">
                {{ displayFirstError(validate.groups.$errors) }}
            </vx-alert>

            <div class="mt-2 flex flex-col gap-3 border bg-slate-100 border-white border-solid shadow rounded-lg p-4">
                <div v-if="groups.length" class="flex items-center gap-2">
                    <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    <span class="text-xs font-semibold">
                        {{ pluralized('groups', groups.length, true) }}
                    </span>
                </div>

                <vx-button @click="openModal()" data-test="open-groups-modal" color="muted">
                    Choose Groups
                    <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                </vx-button>
            </div>

            <vx-modal v-model:visible="groupsModal" size="lg" :padding="false">
                <page-select-org-unit-groups v-model="selectedGroups" :orgUnitId="props.orgUnitId">
                    <template v-slot:header>What Groups do you want to send from?</template>
                    <template v-slot:subheader>You can choose to send from 1 or more Group(s).</template>
                </page-select-org-unit-groups>

                <template v-slot:footer>
                    <div class="flex flex-col-reverse gap-4 md:flex-row">
                        <vx-button
                            @click="cancel"
                            type="button"
                            data-test="cancel-from-selection"
                            class="grow md:w-20 md:grow-0"
                            size="lg"
                            color="muted"
                        >
                            Cancel
                        </vx-button>
                        <vx-popover class="grow w-full" arrow placement="top" hover>
                            <vx-button
                                @click="confirm"
                                data-test="confirm-from-selection"
                                :disabled="selectedGroups.length === 0"
                                type="button"
                                block
                                size="lg"
                                color="primary"
                            >
                                Confirm <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                            </vx-button>
                            <template v-if="selectedGroups.length === 0" v-slot:content>
                                You must select at least one Group
                            </template>
                        </vx-popover>
                    </div>
                </template>
            </vx-modal>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxButton, VxModal, VxAlert, VxPopover } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faGrid2Plus, faMagnifyingGlass, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { computed, ref, onMounted } from 'vue';
import { helpers, minLength, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { displayFirstError } from '~/utils/validation';
import { pluralized } from '~/components/filters';
import PageSelectOrgUnitGroups from '~/components/general/PageSelectOrgUnitGroups.vue';
import { orgUnitGroupsClient } from '~/services';

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: true,
    },
});

const groups = defineModel({
    type: Array,
    required: true,
    default: () => [],
});

const groupsModal = ref(false);
const selectedGroups = ref([]);

const cancel = () => {
    selectedGroups.value = [...groups.value];
    groupsModal.value = false;
};

const confirm = () => {
    groups.value = [...selectedGroups.value];
    groupsModal.value = false;
};

// Initialize selectedGroups when modal opens
const openModal = () => {
    selectedGroups.value = [...groups.value];
    groupsModal.value = true;
};

// Filter groups on mount to exclude any that are not available in the org unit
onMounted(async () => {
    try {
        const teamId = Spark.state.currentTeam.id;
        const { data } = await orgUnitGroupsClient.listOrgUnitGroups(teamId, props.orgUnitId, {
            minimal: 1,
            per_page: 500,
        });

        const availableGroupIds = data.data.map((group) => group.group_id);
        groups.value = groups.value.filter((group) => availableGroupIds.includes(group.group_id));
    } catch (error) {
        console.warn('Failed to validate groups against org unit:', error);
    }
});

const validate = useVuelidate(
    {
        groups: {
            required: helpers.withMessage('Groups are required', required),
            minLength: helpers.withMessage('Groups are required', minLength(1)),
        },
    },
    computed(() => ({
        groups: groups.value || [],
    }))
);
</script>
